
/**
 * Image optimization utilities for improved performance
 */

// Store preloaded image URLs to avoid duplicate preloading
const preloadedImages = new Set<string>();

/**
 * Preload critical images to improve perceived performance
 * @param urls Array of image URLs to preload
 */
export function preloadCriticalImages(urls: string[]): void {
  if (typeof window === 'undefined' || !Array.isArray(urls)) return;
  
  // Process each URL
  urls.forEach(url => {
    // Skip if already preloaded
    if (preloadedImages.has(url)) return;
    
    // Skip invalid URLs
    if (!url || typeof url !== 'string') return;
    
    try {
      // Add to preloaded set
      preloadedImages.add(url);
      
      // Create link element for preloading
      const link = document.createElement('link');
      link.rel = 'preload';
      link.as = 'image';
      link.href = url;
      
      // Append to head
      document.head.appendChild(link);
      
      // Also create an image object to load into browser cache
      const img = new Image();
      img.src = url;
    } catch (e) {
      console.warn(`Failed to preload image: ${url}`, e);
    }
  });
}

/**
 * Optimize image loading for lazy-loaded images
 * @param url Image URL
 * @param lowQualityPlaceholder Low quality placeholder data URL
 */
export function optimizeImage(url: string, lowQualityPlaceholder?: string): string {
  // Simple function to add appropriate query parameters
  // Can be expanded based on specific optimization needs
  
  // Skip optimization for data URLs or invalid URLs
  if (!url || url.startsWith('data:')) return url;
  
  // If we have a low quality placeholder, return it for initial load
  if (lowQualityPlaceholder && typeof window !== 'undefined') {
    // Store original URL
    const originalUrl = url;
    
    // Check if the actual image should be loaded
    setTimeout(() => {
      const img = new Image();
      img.src = originalUrl;
    }, 1000);
    
    return lowQualityPlaceholder;
  }
  
  return url;
}

/**
 * Image dimensions optimization helper
 * Used to prevent layout shifts from images loading
 * @param width The width of the image in pixels
 * @param height The height of the image in pixels
 * @param className Optional CSS class names
 */
export function getImageSizeStyle(
  width: number | null, 
  height: number | null,
  className?: string
): { aspectRatio?: string; width?: string; className?: string } {
  if (!width || !height) {
    return { className };
  }
  
  return {
    aspectRatio: `${width} / ${height}`,
    className
  };
}

export default {
  preloadCriticalImages,
  optimizeImage,
  getImageSizeStyle
};
